import React, { Component } from "react";
import styled from "styled-components";
import api from "../../services/api";
import toasts from "../../services/toasts";
import { Button } from "../../components/buttons";
import { borderColor } from "../../components/layout";
import DeliveryStatus, { StatusBadge } from "./DeliveryStatus";

const Panel = styled.section`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  background: var(--surface-bg);
  margin-bottom: 26px;
  padding: 20px;
`;
const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  h3 { margin: 0 0 6px; }
`;
const Muted = styled.div`
  color: var(--muted-text);
  font-size: 13px;
  line-height: 1.5;
`;
const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 20px 0;
  input, select {
    color: var(--text-color);
    background: var(--surface-bg);
    border: 1px solid ${borderColor};
    border-radius: 4px;
    padding: 9px;
  }
  input { flex: 1; min-width: 180px; }
`;
const Card = styled.article`
  border: 1px solid ${borderColor};
  border-radius: 6px;
  padding: 16px;
  margin-top: 12px;
`;
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
`;
const Badge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  margin: 6px 6px 6px 0;
  border-radius: 20px;
  background: var(--page-bg);
  border: 1px solid ${borderColor};
  font-size: 12px;
`;
const Detail = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.5;
  strong { margin-right: 5px; }
`;
const Editor = styled.form`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${borderColor};
  label { display: block; margin-bottom: 14px; font-size: 13px; }
  input, textarea {
    display: block;
    width: 100%;
    margin-top: 6px;
    padding: 9px;
    border: 1px solid ${borderColor};
    border-radius: 4px;
    background: var(--surface-bg);
    color: var(--text-color);
    box-sizing: border-box;
  }
  textarea { min-height: 80px; resize: vertical; }
  input[type="number"] { max-width: 220px; }
`;
const Preview = styled.div`
  max-width: 420px;
  background: var(--page-bg);
  border: 1px solid ${borderColor};
  border-radius: 12px;
  padding: 14px;
  margin: 14px 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const errorMessage = error =>
  (error.response && error.response.data && error.response.data.message) || error.message || "Could not save notification settings.";
const modeLabels = { alert: "Visible alert", silent: "Background sync", mixed: "Alert + background sync", data: "Data update" };

export default class Policies extends Component {
  state = { types: [], loading: true, error: "", search: "", group: "All", status: "All", selected: null, draft: null, saving: false, later: false };

  componentDidMount() { this.refresh(); }

  refresh = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const result = await api.push_notifications.policies();
      this.setState({ types: result.types, later: result.later_ai_meetup_pushes_enabled, loading: false });
    } catch (error) {
      this.setState({ loading: false, error: errorMessage(error) });
    }
  };

  update = async (type, values) => {
    this.setState({ saving: true });
    try {
      const policy = await api.push_notifications.updatePolicy(type, values);
      if (type === "__later_ai_meetup_pushes__") {
        const result = await api.push_notifications.policies();
        this.setState({ later: result.later_ai_meetup_pushes_enabled, types: result.types });
      } else {
        this.setState(state => ({ types: state.types.map(item => item.notification_type === type ? { ...item, ...policy } : item) }));
      }
      if (this.props.onChange) this.props.onChange();
      toasts.success("Notification settings saved");
      return true;
    } catch (error) {
      toasts.error(errorMessage(error));
      return false;
    } finally {
      this.setState({ saving: false });
    }
  };

  edit = item => this.setState({ selected: item.notification_type, draft: {
    title_template: item.title_template, message_template: item.message_template,
    trigger_settings: Object.keys(item.settings).reduce((values, key) => ({ ...values,
      [key]: item.trigger_settings[key] === undefined ? item.settings[key].default : item.trigger_settings[key]
    }), {})
  } });

  save = async event => {
    event.preventDefault();
    if (await this.update(this.state.selected, this.state.draft)) this.setState({ selected: null, draft: null });
  };

  reset = async type => {
    this.setState({ saving: true });
    try {
      await api.push_notifications.resetPolicy(type);
      this.setState({ selected: null, draft: null });
      await this.refresh();
      if (this.props.onChange) this.props.onChange();
      toasts.success("Default notification settings restored");
    } catch (error) { toasts.error(errorMessage(error)); }
    finally { this.setState({ saving: false }); }
  };

  renderEditor = item => {
    const { draft, saving } = this.state;
    const hasCopy = item.mode !== "silent" && item.mode !== "data";
    const preview = template => template.replace(/\{\{|\}\}|\{title\}|\{message\}/g,
      token => ({ "{{": "{", "}}": "}", "{title}": "Playground", "{message}": "Your notification's original message appears here." })[token]);
    return (
      <Editor onSubmit={this.save}>
        {hasCopy && <React.Fragment>
          <Muted>Leave text blank to use the app's original copy. Use {"{title}"} or {"{message}"} to include the original text. Overrides apply to visible alerts.</Muted>
          <label>Title override (up to 160 characters)
            <input maxLength={160} value={draft.title_template} placeholder="Use original title"
              disabled={saving} onChange={event => this.setState({ draft: { ...draft, title_template: event.target.value } })} />
          </label>
          <label>Message override (up to 500 characters)
            <textarea maxLength={500} value={draft.message_template} placeholder="Use original message"
              disabled={saving} onChange={event => this.setState({ draft: { ...draft, message_template: event.target.value } })} />
          </label>
          <Muted>Example preview · actual names and details come from the triggering event.</Muted>
          <Preview>
            <strong>{preview(draft.title_template) || "Playground"}</strong>
            <div>{preview(draft.message_template) || "Your notification's original message appears here."}</div>
          </Preview>
        </React.Fragment>}
        {Object.keys(item.settings).map(key => {
          const spec = item.settings[key];
          return <label key={key}>{spec.label}
            <input type="number" required min={spec.min} max={spec.max} step={spec.step}
              value={draft.trigger_settings[key]} disabled={saving}
              onChange={event => this.setState({ draft: { ...draft, trigger_settings: { ...draft.trigger_settings,
                [key]: event.target.value === "" ? "" : Number(event.target.value) } } })} />
          </label>;
        })}
        {!Object.keys(item.settings).length && <Muted>This trigger follows the event described above. Its timing and recipient access rules are defined by the app.</Muted>}
        <Actions style={{ marginTop: 16 }}>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
          <Button type="button" disabled={saving} onClick={() => this.setState({ selected: null, draft: null })}>Cancel</Button>
          <Button type="button" disabled={saving} onClick={() => this.reset(item.notification_type)}>Restore defaults (enables this type)</Button>
        </Actions>
      </Editor>
    );
  };

  render() {
    const { types, loading, error, search, group, status, selected, saving, later } = this.state;
    const groups = Array.from(new Set(types.map(item => item.group)));
    const term = search.toLowerCase().trim();
    const counts = types.reduce((result, item) => {
      const state = item.delivery ? item.delivery.status : "unknown";
      return { ...result, [state]: (result[state] || 0) + 1 };
    }, {});
    const visible = types.filter(item => (group === "All" || item.group === group) &&
      (status === "All" || (item.delivery && item.delivery.status === status)) &&
      [item.name, item.notification_type, item.trigger, item.audience, ...((item.delivery && item.delivery.restrictions) || [])].join(" ").toLowerCase().includes(term));
    return (
      <Panel aria-label="Notification types and triggers">
        <Heading>
          <div><h3>Notification types & triggers</h3>
            <Muted>Control delivery, alert text and supported trigger settings. Changes apply to subsequent sends.</Muted></div>
          {!loading && !error && <Actions aria-label="Notification delivery summary">
            <StatusBadge status="enabled">{counts.enabled || 0} enabled</StatusBadge>
            <StatusBadge status="restricted">{counts.restricted || 0} restricted</StatusBadge>
            <StatusBadge status="disabled">{counts.disabled || 0} disabled</StatusBadge>
          </Actions>}
        </Heading>
        <Muted style={{ marginTop: 12 }}>Status describes delivery configuration, not confirmation that a device received a push. Restricted types are enabled with extra limits. Disabling a type skips its pushes, including pending plan alerts when dispatched. Re-enabling does not replay skipped alerts. Background sync can be paused separately. User permissions and access checks still apply.</Muted>
        {loading ? <Muted style={{ padding: 16 }}>Loading notification settings…</Muted> : error ?
          <div role="alert"><p>{error}</p><Button onClick={this.refresh}>Retry</Button></div> : <React.Fragment>
          <Toggle style={{ marginTop: 16 }}>
            <input type="checkbox" checked={later} disabled={saving}
              onChange={event => this.update("__later_ai_meetup_pushes__", { enabled: event.target.checked })} />
            Enable Later AI meetup pushes (sync, approval, messages and live-opportunity events)
          </Toggle>
          <Muted>This gate applies to the older AI meetup send paths. Plan response, confirmation, cancellation and time-change alerts use their own controls below.</Muted>
          <Filters>
            <input aria-label="Search notification types" placeholder="Search types, triggers or audiences…" value={search}
              onChange={event => this.setState({ search: event.target.value })} />
            <select aria-label="Notification group" value={group} onChange={event => this.setState({ group: event.target.value })}>
              <option value="All">All groups</option>{groups.map(value => <option key={value}>{value}</option>)}
            </select>
            <select aria-label="Notification status" value={status} onChange={event => this.setState({ status: event.target.value })}>
              <option value="All">All statuses</option><option value="enabled">Enabled</option>
              <option value="restricted">Restricted</option><option value="disabled">Disabled</option>
            </select>
          </Filters>
          <Muted>{visible.length} notification {visible.length === 1 ? "type" : "types"}</Muted>
          {visible.map(item => <Card key={item.notification_type}>
            <CardHeader>
              <div><strong>{item.name}</strong><Muted>{item.notification_type}</Muted></div>
              <Actions>
                <StatusBadge status={item.delivery ? item.delivery.status : "unknown"} />
                <Toggle><input type="checkbox" checked={item.enabled} disabled={saving}
                  aria-label={`Enable ${item.name}`} onChange={event => this.update(item.notification_type, { enabled: event.target.checked })} />
                  {item.enabled ? "Type enabled" : "Type disabled"}</Toggle>
                <Button disabled={saving || (selected !== null && selected !== item.notification_type)}
                  onClick={() => selected === item.notification_type ? this.setState({ selected: null, draft: null }) : this.edit(item)}>
                  {selected === item.notification_type ? "Close" : "Configure"}
                </Button>
              </Actions>
            </CardHeader>
            <Badge>{item.group}</Badge><Badge>{modeLabels[item.mode]}</Badge>
            {item.delivery && item.delivery.allowed_user_ids.length > 0 && <Badge>Only user IDs: {item.delivery.allowed_user_ids.join(", ")}</Badge>}
            {item.delivery && item.delivery.excluded_sender_user_ids.length > 0 && <Badge>Excluded sender IDs: {item.delivery.excluded_sender_user_ids.join(", ")}</Badge>}
            {item.delivery && item.delivery.later_enabled !== null && <Badge>Later gate: {item.delivery.later_enabled ? "Enabled" : "Disabled"}</Badge>}
            {(item.title_template || item.message_template) && <Badge>Custom copy</Badge>}
            <Detail><strong>Trigger:</strong>{item.trigger}</Detail>
            <Detail><strong>Audience:</strong>{item.audience}</Detail>
            <DeliveryStatus delivery={item.delivery} />
            {item.note && <Muted style={{ marginTop: 6 }}>{item.note}</Muted>}
            {item.updated_at && <Muted style={{ marginTop: 6 }}>Updated {new Date(item.updated_at).toLocaleString()}</Muted>}
            {selected === item.notification_type && this.renderEditor(item)}
          </Card>)}
          {!visible.length && <Muted style={{ padding: 16 }}>No notification types match these filters.</Muted>}
        </React.Fragment>}
      </Panel>
    );
  }
}
